import type { DashboardFilters } from '@/features/dashboard/api/schemas'

/** The URL parameters holding the filters: `?periods=6&market=DEU&market=FRA`. */
const PERIODS_PARAM = 'periods'
const MARKET_PARAM = 'market'

export type FilterSelection = {
  periods: number
  /** The selected market ids, in the order the API lists them. Never empty. */
  markets: string[]
}

export const allMarketIds = (filters: DashboardFilters) =>
  filters.markets.map(market => market.id)

const defaultSelection = (filters: DashboardFilters): FilterSelection => ({
  periods: filters.periods.default,
  markets: allMarketIds(filters),
})

/** Reads the selection from the URL, ignoring values the dashboard doesn't offer. */
export const parseSelection = (
  params: URLSearchParams,
  filters: DashboardFilters
): FilterSelection => {
  const defaults = defaultSelection(filters)
  // Absent is the default, not 0 (all time).
  const requestedPeriods = params.get(PERIODS_PARAM)
  const periods = requestedPeriods === null ? NaN : Number(requestedPeriods)
  const requested = new Set(params.getAll(MARKET_PARAM))
  const markets = defaults.markets.filter(id => requested.has(id))
  return {
    periods: filters.periods.options.some(option => option.value === periods)
      ? periods
      : defaults.periods,
    markets: markets.length > 0 ? markets : defaults.markets,
  }
}

/**
 * Writes the selection to a copy of the parameters, leaving others alone. A
 * selection equal to the defaults leaves no parameters, keeping the URL clean.
 */
export const writeSelection = (
  params: URLSearchParams,
  selection: FilterSelection,
  filters: DashboardFilters
): URLSearchParams => {
  const next = new URLSearchParams(params)
  next.delete(PERIODS_PARAM)
  next.delete(MARKET_PARAM)
  if (selection.periods !== filters.periods.default) {
    next.set(PERIODS_PARAM, String(selection.periods))
  }
  if (selection.markets.length < filters.markets.length) {
    selection.markets.forEach(id => next.append(MARKET_PARAM, id))
  }
  return next
}

/**
 * Like bristol: with every market selected (no filter) a click selects only
 * that one; otherwise it toggles, and removing the last one resets to all.
 */
export const toggleMarket = (
  selected: string[],
  allIds: string[],
  id: string
): string[] => {
  if (selected.length === allIds.length) return [id]
  const next = selected.includes(id)
    ? selected.filter(existing => existing !== id)
    : allIds.filter(existing => existing === id || selected.includes(existing))
  return next.length > 0 ? next : allIds
}
